import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Pencil, Plus, Trash2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Prize } from "@/types";

interface PrizesManagementProps {
  prizes: Prize[];
  onRefresh: () => void;
}

export function PrizesManagement({ prizes, onRefresh }: PrizesManagementProps) {
  const { toast } = useToast();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingPrize, setEditingPrize] = useState<Prize | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const handleDelete = async (id: number) => {
    try {
      setDeleteError(null);
      console.log('Starting delete operation for prize ID:', id);
      
      // First check if there are any winners associated with this prize
      const { data: winners, error: winnersError } = await supabase
        .from('winners')
        .select('id')
        .eq('prize_id', id);
      
      console.log('Winners check result:', { winners, winnersError });
        
      if (winnersError) {
        console.error('Error checking winners:', winnersError);
        throw new Error('Failed to check if prize is in use');
      }

      if (winners && winners.length > 0) {
        const errorMessage = `This prize cannot be deleted because it has been won ${winners.length} time${winners.length === 1 ? '' : 's'}. Delete the associated winners first.`;
        setDeleteError(errorMessage);
        console.error('Prize is referenced by winners:', winners);
        throw new Error(errorMessage);
      }

      console.log('No winners found, proceeding with deletion');

      // If no winners reference this prize, proceed with deletion
      const { error: deleteError } = await supabase
        .from('prizes')
        .delete()
        .eq('id', id);

      if (deleteError) {
        console.error('Delete error:', deleteError);
        throw deleteError;
      }
      
      toast({ 
        title: "Success", 
        description: "Prize deleted successfully" 
      });
      
      console.log('Refreshing prizes list');
      onRefresh();
      
    } catch (error: any) {
      console.error('Delete operation failed:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete prize",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      console.log('Submitting form data:', formData);
      console.log('Editing prize:', editingPrize);

      const prizeData = {
        name: formData.name,
        description: formData.description || null,
      };

      if (editingPrize) {
        const { error } = await supabase
          .from("prizes")
          .update(prizeData)
          .eq("id", editingPrize.id);
        if (error) throw error;
        toast({ title: "Success", description: "Prize updated successfully" });
      } else {
        const { error } = await supabase.from("prizes").insert([prizeData]);
        if (error) throw error;
        toast({ title: "Success", description: "Prize added successfully" });
      }
      
      setFormData({ name: "", description: "" });
      setIsAddOpen(false);
      setIsEditOpen(false);
      onRefresh();
    } catch (error: any) {
      console.error("Error saving prize:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save prize",
      });
    }
  };

  const handleEdit = (prize: Prize) => {
    setEditingPrize(prize);
    setFormData({
      name: prize.name,
      description: prize.description || "",
    });
    setIsEditOpen(true);
  };

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Prizes Management</h2>
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-2">
              <Plus className="h-4 w-4" /> Add Prize
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Prize</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                placeholder="Prize Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <Textarea
                placeholder="Prize Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <Button type="submit">Add Prize</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {deleteError && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{deleteError}</AlertDescription>
        </Alert>
      )}

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Prize</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              placeholder="Prize Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Textarea
              placeholder="Prize Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <Button type="submit">Update Prize</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Created At</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {prizes.map((prize) => (
            <TableRow key={prize.id}>
              <TableCell>{prize.name}</TableCell>
              <TableCell>{prize.description}</TableCell>
              <TableCell>{new Date(prize.created_at).toLocaleDateString()}</TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(prize)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(prize.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}